import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calculator, Columns3, Download, Loader2 } from "lucide-react";
import { CODES, GRADES, bestWidth, sizeBeam, variableLoad } from "@/lib/beamCalc";
import { sizePanel } from "@/lib/xlamCalc";
import { EmailGate, savedContact } from "./EmailGate";
import { CalcCuts, beamList } from "./CalcCuts";
import { scrollToId } from "@/lib/scroll";
import { useLang, useT } from "@/i18n";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WIDTHS = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30];
const MODES = [["beam", "Trave appoggiata"], ["floor", "Solaio"], ["roof", "Copertura a falde"], ["xlam", "Solaio X-LAM"]];
// NTC 2018 Tab. 3.1.II: [id, label, qk kN/m², ψ2]
const CATS = [["A", "A · Residenziale", 2, 0.3], ["B1", "B1 · Uffici non aperti al pubblico", 2, 0.3], ["B2", "B2 · Uffici aperti al pubblico", 3, 0.3], ["C1", "C1 · Scuole, ristoranti, caff\u00e8", 3, 0.6], ["C2", "C2 · Chiese, teatri, cinema", 4, 0.6], ["C3", "C3 · Musei, palestre, sale espositive", 5, 0.6]];
const ZONES = [["I-A", "I \u00b7 Alpina"], ["I-M", "I \u00b7 Mediterranea"], ["II", "II"], ["III", "III"]];
const fmt = (n, d = 0) => n.toLocaleString("it-IT", { maximumFractionDigits: d, minimumFractionDigits: d });
// Loads are entered and shown in kg/m²; the calculation engines work in kN/m².
const KN_PER_KG = 0.00980665;
const kg = (kn) => Math.round(kn / KN_PER_KG);

const Num = ({ id, label, value, onChange, step = "0.1" }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{label}</span>
    <input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`calc-${id}`} />
  </label>
);

const Pick = ({ id, label, value, onChange, options }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#A39E93]">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="field bg-transparent [&>option]:bg-[#1B1816]" data-testid={`calc-${id}`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const Bar = ({ label, v, id }) => (
  <div data-testid={`calc-ratio-${id}`}>
    <div className="flex justify-between text-sm"><span className="text-[#D6D0C4]">{label}</span><span className="tabular-nums">{fmt(v * 100)}%</span></div>
    <div className="mt-1 h-1.5 bg-white/10"><div className="h-full bg-[#D98A52] transition-[width] duration-500" style={{ width: `${Math.min(100, v * 100)}%` }} /></div>
  </div>
);

const ModeTabs = ({ mode, setMode, t }) => (
  <div className="mt-10 inline-flex flex-wrap gap-2" role="tablist" data-testid="calc-modes">
    {MODES.map(([m, l]) => (
      <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} data-testid={`calc-mode-${m}`}
        className={`tap rounded-full border px-5 py-2 text-sm transition-colors duration-300 ${mode === m ? "border-[#D98A52] bg-[#D98A52] text-[#14110F]" : "border-white/20 text-[#D6D0C4] hover:border-[#D98A52]"}`}>
        {t(`calc.m.${m}`, l)}
      </button>
    ))}
  </div>
);

const Fields = ({ f, set, t }) => (
  <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7">
    <Num id="L" label={t(f.mode === "roof" ? "calc.Lh" : "calc.L", f.mode === "roof" ? "Luce in pianta (m)" : "Luce (m)")} value={f.L} onChange={set("L")} />
    {f.mode !== "xlam" && <Num id="i" label={t("calc.i", "Interasse (m)")} value={f.i} onChange={set("i")} />}
    <Num id="G" label={t("calc.G", "Permanenti portati (kg/m²)")} value={f.G} onChange={set("G")} step="10" />
    {f.mode === "beam" && <Num id="Q" label={t("calc.Q", "Variabili (kg/m²)")} value={f.Q} onChange={set("Q")} step="10" />}
    {(f.mode === "floor" || f.mode === "xlam") && <Pick id="cat" label={t("calc.cat", "Categoria d'uso (NTC 2018)")} value={f.cat} onChange={set("cat")} options={CATS.map(([id, l, q]) => [id, `${t(`calc.cat.${id}`, l)} \u2014 ${fmt(kg(q))} kg/m²`])} />}
    {f.mode === "roof" && <>
      <Num id="pitch" label={t("calc.pitch", "Pendenza falda (°)")} value={f.pitch} onChange={set("pitch")} step="1" />
      <Pick id="zone" label={t("calc.zone", "Zona neve (NTC 2018)")} value={f.zone} onChange={set("zone")} options={ZONES.map(([z, l]) => [z, t(`calc.zone.${z}`, l)])} />
      <Num id="alt" label={t("calc.alt", "Altitudine del sito (m s.l.m.)")} value={f.alt} onChange={set("alt")} step="10" />
    </>}
    {f.mode !== "xlam" && <>
      <Pick id="grade" label={t("calc.grade", "Classe di resistenza")} value={f.grade} onChange={set("grade")} options={Object.keys(GRADES).map((g) => [g, g])} />
      <Pick id="b" label={t("calc.b", "Larghezza b (cm)")} value={f.b} onChange={set("b")} options={WIDTHS.map((w) => [String(w), `${w} cm`])} />
    </>}
    <Pick id="sc" label={t("calc.sc", "Classe di servizio")} value={f.sc} onChange={set("sc")} options={[["1", "1"], ["2", "2"]]} />
    <Pick id="code" label={t("calc.code", "Norma")} value={f.code} onChange={set("code")} options={Object.entries(CODES).map(([k, c]) => [k, c.label])} />
  </div>
);

const toParams = (f) => {
  const cat = CATS.find(([id]) => id === f.cat) ?? CATS[0];
  const p = { mode: f.mode, L: +f.L, i: f.mode === "xlam" ? 1 : +f.i, G: +f.G * KN_PER_KG, Q: f.mode === "floor" || f.mode === "xlam" ? cat[2] : +f.Q * KN_PER_KG, psi2: cat[3], pitch: +f.pitch, zone: f.zone, alt: +f.alt, grade: f.mode === "xlam" ? "C24" : f.grade, sc: +f.sc, code: f.code, b: f.mode === "xlam" ? 100 : +f.b };
  return { ...p, ...(f.mode === "xlam" ? {} : variableLoad(p)), cat: f.mode === "floor" || f.mode === "xlam" ? cat[1] : "" };
};

const Result = ({ r, p, t, onPdf, pdfBusy }) => {
  const ask = () => {
    const base = p.mode === "xlam"
      ? t("calc.msgX", "Vorrei verificare un solaio X-LAM: luce {L} m, permanenti {G} kg/m², variabili {Q} kg/m². Stima rapida: pannello {h} mm.")
      : t("calc.msg", "Vorrei verificare una trave: luce {L} m, interasse {i} m, permanenti {G} kg/m², variabili {Q} kg/m², {grade}. Stima rapida: {b} × {h} cm.");
    const message = base.replace("{L}", p.L).replace("{i}", p.i).replace("{G}", fmt(kg(p.G))).replace("{Q}", fmt(kg(p.Q))).replace("{grade}", p.grade).replace("{b}", p.b).replace("{h}", r?.h ?? "\u2014");
    window.dispatchEvent(new CustomEvent("prefill-quote", { detail: { project_type: p.mode === "xlam" ? "Pannelli X-LAM" : "Legno lamellare BSH", message } }));
    scrollToId("#contatti");
  };
  return (
    <div data-testid="calc-result">
      {p.mode === "roof" && <p className="mb-4 text-sm text-[#D6D0C4]" data-testid="calc-snow">{t("calc.snow", "Carico neve sulla copertura")}: <b className="text-[#F3EFE6]">{fmt(kg(p.Q))} kg/m²</b></p>}
      {r ? <>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">{t(p.mode === "xlam" ? "calc.resX" : "calc.res", p.mode === "xlam" ? "Pannello consigliato" : "Sezione stimata")}</p>
        {p.mode === "xlam" ? <>
          <p className="mt-2 font-display text-5xl font-bold tabular-nums" data-testid="calc-section">{r.thick} <span className="text-2xl">mm</span></p>
          <p className="mt-2 text-sm text-[#D6D0C4]" data-testid="calc-panel">{r.layers} {t("calc.layers", "strati")} · {r.comp} · C24 · {t("calc.defl", "freccia finale")} {fmt(r.wFin, 1)} mm</p>
        </> : <>
          <p className="mt-2 font-display text-5xl font-bold tabular-nums" data-testid="calc-section">{p.b} × {r.h} <span className="text-2xl">cm</span></p>
          <p className="mt-2 text-sm text-[#D6D0C4]">{p.grade} · {t("calc.defl", "freccia finale")} {fmt(r.wFin, 1)} mm</p>
        </>}
        <div className="mt-6 space-y-3">
          <Bar id="bending" label={t("calc.r0", "Flessione")} v={r.bending} />
          <Bar id="shear" label={p.mode === "xlam" ? t("calc.r1x", "Taglio per rotolamento") : t("calc.r1", "Taglio")} v={r.shear} />
          <Bar id="inst" label={t("calc.r2", "Freccia istantanea (L/300)")} v={r.inst} />
          <Bar id="fin" label={t("calc.r3", "Freccia finale (L/250)")} v={r.fin} />
        </div>
      </> : <p className="text-[#E9A06B]" data-testid="calc-none">{p.mode === "xlam" ? t("calc.noneX", "Nessun pannello fino a 300 mm soddisfa le verifiche: riduci la luce o prevedi un appoggio intermedio.") : t("calc.none", "Nessuna sezione fino a 280 cm di altezza soddisfa le verifiche: aumenta la larghezza o riduci l'interasse.")}</p>}
      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={onPdf} disabled={pdfBusy} className="inline-flex items-center gap-2 rounded-full border border-[#D98A52] px-5 py-3 text-sm text-[#F3EFE6] transition-colors duration-300 hover:bg-[#D98A52]/15 disabled:opacity-60" data-testid="calc-pdf">
          {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t("calc.pdf", "Scarica il calcolo in PDF")}
        </button>
        <button onClick={ask} className="btn-amber" data-testid="calc-cta">{p.mode === "xlam" ? t("calc.ctaX", "Fai verificare il solaio al nostro ufficio tecnico") : t("calc.cta", "Fai verificare la trave al nostro ufficio tecnico")} <ArrowRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

// Three-class comparison (GL24h, GL28h, GL32h): same span and loads, with the user's width and the
// most convenient width (least timber) for each class. Glulam modes only.
const vol = (b, h) => (b * h) / 10000;
const Card = ({ row, b, mine, t }) => {
  const { grade, r, best } = row;
  return (
    <div className={`border p-5 sm:p-6 ${mine ? "border-[#D98A52]" : "border-white/15"}`} data-testid={`calc-cmp-${grade}`}>
      <p className="flex items-center justify-between gap-2 font-display text-xl font-bold">{grade}
        {mine && <span className="rounded-full bg-[#D98A52]/15 px-2 py-0.5 font-mono text-xs uppercase tracking-[0.15em] text-[#D98A52]">{t("calc.cmpMine", "la tua scelta")}</span>}</p>
      {r ? <>
        <p className="mt-3 font-display text-3xl font-bold tabular-nums" data-testid={`calc-cmp-${grade}-section`}>{b} × {r.h} <span className="text-lg">cm</span></p>
        <dl className="mt-4 space-y-1 text-sm text-[#D6D0C4]">
          <div className="flex justify-between gap-3"><dt>{t("calc.defl", "freccia finale")}</dt><dd className="tabular-nums">{fmt(r.wFin, 1)} mm</dd></div>
          <div className="flex justify-between gap-3"><dt>{t("calc.cmpUse", "sfruttamento massimo")}</dt><dd className="tabular-nums">{fmt(Math.max(r.bending, r.shear, r.inst, r.fin) * 100)}%</dd></div>
          <div className="flex justify-between gap-3"><dt>{t("calc.cmpVol", "legno per metro")}</dt><dd className="tabular-nums" data-testid={`calc-cmp-${grade}-vol`}>{fmt(vol(b, r.h), 3)} m³</dd></div>
        </dl>
      </> : <p className="mt-3 text-sm text-[#E9A06B]">{t("calc.cmpNone", "Nessuna sezione soddisfa le verifiche.")}</p>}
      <div className="mt-5 border-t border-white/10 pt-4" data-testid={`calc-cmp-${grade}-best`}>
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#D98A52]">{t("calc.cmpBest", "Larghezza più conveniente")}</p>
        {best ? <p className="mt-2 text-sm text-[#D6D0C4]"><b className="text-lg text-[#F3EFE6] tabular-nums">{best.b} × {best.h} cm</b> · {fmt(vol(best.b, best.h), 3)} m³</p>
          : <p className="mt-2 text-sm text-[#E9A06B]">{t("calc.cmpNone", "Nessuna sezione soddisfa le verifiche.")}</p>}
      </div>
    </div>
  );
};

const cmpNote = (rows, p, r, t) => {
  const opts = rows.filter((x) => x.best);
  if (!r || !opts.length) return "";
  const top = opts.reduce((a, x) => (vol(x.best.b, x.best.h) < vol(a.best.b, a.best.h) ? x : a));
  const less = Math.round((1 - vol(top.best.b, top.best.h) / vol(p.b, r.h)) * 100);
  if (less <= 0) return t("calc.cmpBestMine", "La tua soluzione è già quella con meno legno.");
  return t("calc.cmpBestNote", "La soluzione con meno legno è {g} {s} cm: {p}% di legno in meno della tua.")
    .replace("{g}", top.grade).replace("{s}", `${top.best.b} × ${top.best.h}`).replace("{p}", less);
};

const Compare = ({ p, r, rows, on, setOn, valid, t }) => {
  const toggle = () => { if (!on) track("confronto_calcolo", { tipo: p.mode }); setOn(!on); };
  return (
    <div className="mt-10" data-testid="calc-compare">
      <button onClick={toggle} aria-expanded={on} className="tap inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2 text-sm text-[#D6D0C4] transition-colors duration-300 hover:border-[#D98A52]" data-testid="calc-compare-toggle">
        <Columns3 className="h-4 w-4 text-[#D98A52]" /> {on ? t("calc.cmpOff", "Chiudi il confronto") : t("calc.cmpOn3", "Confronta GL24h, GL28h e GL32h")}
      </button>
      {on && valid && <>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {rows.map((row) => <Card key={row.grade} row={row} b={p.b} mine={row.grade === p.grade} t={t} />)}
        </div>
        <p className="mt-4 text-sm font-semibold text-[#F3EFE6]" data-testid="calc-compare-note">{cmpNote(rows, p, r, t)}</p>
        <p className="mt-2 text-sm text-[#D6D0C4]">{t("calc.cmpHow", "Stessa luce e stessi carichi. Larghezza più conveniente: quella con meno legno tra 10 e 30 cm, con altezza fino a 10 volte la larghezza.")} {t("calc.cmpPdf", "Il confronto viene incluso nel PDF scaricato.")}</p>
      </>}
    </div>
  );
};

const saveFile = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// fixed: one calculator only (no tabs), with its own anchor id, heading and intro (used by /calcolatori)
export const SectionCalc = ({ initialMode = "beam", fixed = false, id = "calcolo", title, intro }) => {
  const t = useT();
  const { lang } = useLang();
  const [f, setF] = useState({ mode: initialMode, L: initialMode === "xlam" ? "5" : "10", i: "1", G: "200", Q: "200", cat: "A", pitch: "20", zone: "II", alt: "200", grade: "GL24h", sc: "1", code: "ntc", b: "16", rows: [{ n: "1", len: "" }] });
  const [gate, setGate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileFmt, setFileFmt] = useState("pdf"); // pdf = calculation, xlsx = cutting plan for production
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  // ?calc=beam|floor|roof|xlam (from the menu) opens that calculator and brings it into view
  const { search } = useLocation();
  useEffect(() => {
    const m = new URLSearchParams(search).get("calc");
    if (fixed || !MODES.some(([k]) => k === m)) return undefined;
    setF((s) => ({ ...s, mode: m }));
    const id = setTimeout(() => scrollToId("#calcolo"), 150);
    return () => clearTimeout(id);
  }, [search, fixed]);
  const p = useMemo(() => toParams(f), [f]);
  const valid = p.L > 0 && p.L <= 44 && p.i > 0 && p.G >= 0 && p.Q >= 0 && p.pitch >= 0 && p.pitch <= 60 && p.alt >= 0 && p.alt <= 3000;
  const r = useMemo(() => (valid ? (p.mode === "xlam" ? sizePanel(p) : sizeBeam(p)) : null), [valid, p]);
  // comparison of the three glulam classes (with the most convenient width for each); also sent to the PDF
  const [cmpOpen, setCmpOpen] = useState(false);
  const cmpOn = cmpOpen && valid && p.mode !== "xlam";
  const rows = useMemo(() => (cmpOn ? Object.keys(GRADES).map((g) => ({
    grade: g, r: g === p.grade ? r : sizeBeam({ ...p, grade: g }), best: bestWidth({ ...p, grade: g }, WIDTHS),
  })) : []), [cmpOn, p, r]);
  const pick = (x) => x && { h: x.h, wFin: x.wFin, bending: x.bending, shear: x.shear, inst: x.inst, fin: x.fin };

  const download = async (form, format = fileFmt) => {
    const params = { mode: p.mode, L: p.L, i: p.i, G: kg(p.G), Q: kg(p.Q), pitch: p.pitch, zone: p.zone, alt: p.alt, cat: p.cat, grade: p.grade, b: p.b, sc: p.sc, code_label: CODES[p.code].label, panel: r?.comp ? `${r.layers} · ${r.comp}` : "" };
    if (p.mode !== "xlam") {
      const list = beamList(p, r, f);
      if (list.ok) params.rows = list.rows;
    }
    const result = pick(r);
    const compare = cmpOn ? rows.map((x) => ({ grade: x.grade, result: pick(x.r), best: x.best })) : null;
    const res = await axios.post(`${API}/calc-pdf`, { ...form, lang, params, result, compare, format }, { responseType: "blob" });
    const xlsx = format === "xlsx";
    saveFile(res.data, xlsx ? "piano-di-taglio-emma-industrie.xlsx" : "calcolo-trave-emma-industrie.pdf");
    track(xlsx ? "download_piano_taglio" : "download_calcolo", { tipo: p.mode, lingua: lang });
    setGate(false);
    toast.success(xlsx ? t("cuts.xlsxOk", "Excel scaricato: piano di taglio trave per trave e distinta.") : t("calc.pdfOk", "PDF scaricato: trovi il calcolo e la scheda di lavoro già compilata."));
  };
  const onFile = (format) => async () => {
    setFileFmt(format);
    const contact = savedContact();
    if (!contact) return setGate(true);
    setBusy(format);
    try { await download({ ...contact, consent: true }, format); } catch { setGate(true); } finally { setBusy(false); }
  };
  const onPdf = onFile("pdf");

  return (
    <section id={id} className={`bg-[#1A1714] scroll-mt-28 px-5 ${fixed ? "border-t border-white/10" : ""}  py-24 text-[#F3EFE6] sm:px-8`} data-testid={fixed ? `calc-wrap-${initialMode}` : "calc-section-wrap"}>
      <div className="mx-auto max-w-[1500px]">
        <Calculator className="h-8 w-8 text-[#D98A52]" />
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">{title ?? t("calc.h", "Calcolo rapido della sezione")}</h2>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#D6D0C4]">{intro ?? t("calc.p2", "Scegli l'elemento, inserisci luce e carichi: stimiamo l'altezza minima della trave in lamellare verificando flessione, taglio e freccia. Per le coperture calcoliamo il carico neve secondo le NTC 2018.")}</p>
        {!fixed && <ModeTabs mode={f.mode} setMode={set("mode")} t={t} />}
        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <Fields f={f} set={set} t={t} />
          <div className="border border-white/15 p-6 sm:p-8 lg:col-span-5">
            {valid ? <Result r={r} p={p} t={t} onPdf={onPdf} pdfBusy={busy === "pdf"} /> : <p data-testid="calc-invalid">{t("calc.invalid2", "Controlla i valori: luce tra 0 e 44 m, pendenza tra 0° e 60°, altitudine fino a 3000 m.")}</p>}
          </div>
        </div>
        {f.mode !== "xlam" && valid && <CalcCuts p={p} r={r} f={f} set={set} t={t} onXlsx={onFile("xlsx")} xlsxBusy={busy === "xlsx"} />}
        {f.mode !== "xlam" && <Compare p={p} r={r} rows={rows} on={cmpOpen} setOn={setCmpOpen} valid={valid} t={t} />}
        <p className="mt-8 max-w-4xl text-xs leading-relaxed text-[#A39E93]" data-testid="calc-disclaimer">
          {t("calc.note2", "Stima preliminare, non sostituisce il calcolo strutturale di un professionista abilitato. Ipotesi: elemento appoggiato alle estremità e vincolato contro lo svergolamento, carichi di media durata (kmod 0,8), peso proprio 510 kg/m³, combinazione SLU {gG}·G + {gQ}·Q, γM {gM}, altezze a passo 4 cm. Coperture: neve qs = μ1·qsk (CE = Ct = 1), luce misurata in pianta, permanenti sulla falda.")
            .replace("{gG}", fmt(CODES[f.code].gG, 2)).replace("{gQ}", fmt(CODES[f.code].gQ, 1)).replace("{gM}", fmt(CODES[f.code].gM, 2))}
          {f.mode === "xlam" && ` ${t("calc.noteX", "Solaio X-LAM: striscia di 1 m in semplice appoggio, pannelli e valori C24 della tabella EMMA INDUSTRIE, metodo gamma con taglio per rotolamento degli strati trasversali, kdef 0,8 / 1,0. Vibrazioni e resistenza al fuoco non verificate.")}`}
        </p>
      </div>
      <EmailGate open={gate} onClose={() => setGate(false)} title={fileFmt === "xlsx" ? t("cuts.xlsxGateH", "Scarica il piano di taglio in Excel") : t("calc.pdf", "Scarica il calcolo in PDF")}
        description={fileFmt === "xlsx" ? t("cuts.xlsxGate", "Lascia nome, cognome, email e telefono per scaricare il piano di taglio in Excel, trave per trave, pronto per la produzione.") : t("calc.gate2", "Lascia nome, cognome, email e telefono per scaricare il calcolo in PDF con la scheda di lavoro già compilata.")}
        submitLabel={fileFmt === "xlsx" ? t("cuts.xlsx", "Scarica il piano in Excel") : t("pdf.again", "Scarica il PDF")} onSubmit={(form) => download(form)} />
    </section>
  );
};
